import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiSearch, FiUnlock } from "react-icons/fi";
import PaginationControls from "../../PaginationControls";
import UnlockSubmissionModal from "./UnlockSubmissionModal";
import {
  fetchApprovedTimesheetSubmissions,
  fetchTimesheetDashboardUsers,
} from "../../../services/timesheetApi";
import { formatHours, getEntityId, normalizeDateKey } from "../../../utils/timesheetUtils";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_FILTERS = [
  { value: "approved", label: "อนุมัติแล้ว" },
  { value: "unlocked", label: "เคยปลดล็อก" },
];

const formatDate = (value) => {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return "-";
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("th-TH");
};

const getApprovers = (steps = []) =>
  steps
    .filter((step) => String(step?.status || "").toLowerCase() === "approved")
    .map((step) => `Lv.${step.level ?? "?"} ${step.approverUsername || "-"}`);

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100";

const ApprovedSubmissionsTab = ({ refreshKey }) => {
  const [status, setStatus] = useState("approved");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [unlockTarget, setUnlockTarget] = useState(null);
  // กัน response ของ request เก่ามาทับผลของตัวกรองล่าสุด
  const requestIdRef = useRef(0);

  // from/to ต้องส่งคู่กัน ถ้าเลือกไม่ครบหรือกลับด้านจะไม่ส่งไป backend
  const dateRangeIncomplete = Boolean(from) !== Boolean(to);
  const dateRangeInvalid = Boolean(from && to && from > to);
  const appliedFrom = from && to && !dateRangeInvalid ? from : "";
  const appliedTo = from && to && !dateRangeInvalid ? to : "";

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    fetchTimesheetDashboardUsers()
      .then((response) => {
        if (!cancelled) setEmployees(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        // ไม่มีสิทธิ์ดูรายชื่อ ก็ซ่อน dropdown พนักงานไป ยังกรองด้วยช่องค้นหาได้
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSubmissions = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError("");
      const result = await fetchApprovedTimesheetSubmissions({
        status,
        userId,
        from: appliedFrom,
        to: appliedTo,
        search,
        page,
        limit: PAGE_SIZE,
      });
      if (requestId !== requestIdRef.current) return;
      setForbidden(false);
      setItems(result.data);
      setPagination(result.pagination);
      const totalPages = Number(result.pagination?.totalPages || 0);
      if (totalPages > 0 && page > totalPages) setPage(totalPages);
    } catch (requestError) {
      if (requestId !== requestIdRef.current) return;
      setItems([]);
      if (requestError?.response?.status === 403) {
        setForbidden(true);
        return;
      }
      setError(requestError?.response?.data?.message || "ไม่สามารถโหลดรายการ Timesheet ที่อนุมัติแล้วได้");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [appliedFrom, appliedTo, page, search, status, userId]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions, refreshKey]);

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const closeUnlockModal = useCallback(() => setUnlockTarget(null), []);

  const handleUnlockFinished = useCallback(() => {
    setUnlockTarget(null);
    loadSubmissions();
  }, [loadSubmissions]);

  if (forbidden) {
    return (
      <div className="rounded-3xl border border-gray-200 bg-white px-6 py-12 text-center text-sm font-medium text-red-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-red-300">
        คุณไม่มีสิทธิ์ดูรายการ Timesheet ที่อนุมัติแล้ว
      </div>
    );
  }

  const isUnlockedView = status === "unlocked";

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="inline-flex shrink-0 rounded-xl bg-gray-100 p-1 dark:bg-gray-900" role="group" aria-label="สถานะ">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setStatus(option.value);
                  setPage(1);
                }}
                aria-pressed={status === option.value}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  status === option.value
                    ? "bg-white text-indigo-700 shadow-sm dark:bg-gray-700 dark:text-indigo-200"
                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="relative block">
              <span className="sr-only">ค้นหาชื่อหรืออีเมล</span>
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="ค้นหาชื่อ / อีเมล" className={`${inputClass} pl-9`} />
            </label>
            {employees.length > 0 && (
              <select value={userId} onChange={updateFilter(setUserId)} className={inputClass} aria-label="พนักงาน">
                <option value="">พนักงานทั้งหมด</option>
                {employees.map((employee) => {
                  const id = getEntityId(employee._id || employee.id);
                  return <option key={id} value={id}>{employee.displayName || employee.username || employee.email || id}</option>;
                })}
              </select>
            )}
            <input type="date" value={from} max={to || undefined} onChange={updateFilter(setFrom)} className={inputClass} aria-label="ตั้งแต่วันที่" />
            <input type="date" value={to} min={from || undefined} onChange={updateFilter(setTo)} className={inputClass} aria-label="ถึงวันที่" />
          </div>
        </div>
        {(dateRangeIncomplete || dateRangeInvalid) && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            {dateRangeInvalid ? "วันเริ่มต้องไม่เกินวันสิ้นสุด" : "เลือกให้ครบทั้งวันเริ่มและวันสิ้นสุดเพื่อกรองตามช่วงวันที่"}
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-busy={loading}>
        {loading ? (
          <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">กำลังโหลดรายการ...</p>
        ) : error ? (
          <p className="px-6 py-12 text-center text-sm font-medium text-red-600 dark:text-red-300">{error}</p>
        ) : !items.length ? (
          <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">
            {isUnlockedView ? "ไม่มี Timesheet ที่เคยปลดล็อก" : "ไม่มี Timesheet ที่อนุมัติแล้ว"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr>
                  <th className="px-4 py-3 text-left">พนักงาน</th>
                  <th className="px-4 py-3 text-left">ช่วงสัปดาห์</th>
                  <th className="px-4 py-3 text-right">ชั่วโมงรวม</th>
                  <th className="px-4 py-3 text-left">วันที่อนุมัติ</th>
                  <th className="px-4 py-3 text-left">ผู้อนุมัติ</th>
                  {isUnlockedView && <th className="px-4 py-3 text-left">การปลดล็อก</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="[&>tr:nth-child(even)]:bg-slate-50/80 dark:[&>tr:nth-child(even)]:bg-gray-900/35">
                {items.map((item) => {
                  const user = item.user || {};
                  const approvers = getApprovers(item.approvalSteps);
                  return (
                    <tr key={item._id} className="border-t border-gray-100 align-top dark:border-gray-700/80">
                      <td className="max-w-[220px] px-4 py-4">
                        <p className="break-all font-medium text-gray-900 dark:text-gray-100">{user.displayName || user.username || "-"}</p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{user.department || "-"}</p>
                      </td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{formatDate(item.periodStart)} - {formatDate(item.periodEnd)}</td>
                      <td className="px-4 py-4 text-right text-gray-700 dark:text-gray-200">{formatHours(item.totalHours)}</td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{formatDateTime(item.approvedAt)}</td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">
                        {approvers.length ? approvers.map((name) => <p key={name} className="break-all">{name}</p>) : "-"}
                      </td>
                      {isUnlockedView && (
                        <td className="max-w-[260px] px-4 py-4 text-gray-600 dark:text-gray-300">
                          <p className="font-medium text-gray-800 dark:text-gray-100">{item.unlockedBy?.displayName || item.unlockedBy?.username || "-"}</p>
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{formatDateTime(item.unlockedAt)}</p>
                          {item.unlockReason && <p className="mt-1 break-words text-xs">เหตุผล: {item.unlockReason}</p>}
                        </td>
                      )}
                      <td className="px-4 py-4 text-right">
                        {item.canUnlock === true && (
                          <button type="button" onClick={() => setUnlockTarget(item)} className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-500/10">
                            <FiUnlock /> ปลดล็อก
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">ทั้งหมด {pagination.total} รายการ</p>
          <PaginationControls currentPage={page} totalPages={pagination.totalPages} onPageChange={setPage} />
        </div>
      )}

      <UnlockSubmissionModal
        key={unlockTarget?._id || "closed"}
        submission={unlockTarget}
        onClose={closeUnlockModal}
        onUnlocked={handleUnlockFinished}
        onConflict={handleUnlockFinished}
      />
    </div>
  );
};

export default ApprovedSubmissionsTab;
